/**
 * The `activeMaterialFilters` groups that HIDE inventory rows.
 *
 * `sorting` shares the same object but only reorders what is already on screen, so it is
 * deliberately absent: it can never be the reason the list came back empty, and wiping it would
 * strand the toolbar's own sort chips (`sortState` in `ActionsInventory.vue`) out of sync.
 */
export const INVENTORY_HIDING_FILTER_GROUPS = ["mappings", "states", "datasources", "classificationCodes"] as const;

/**
 * Is this filter group narrowing the list? A group is a `{ [optionId]: boolean }` map, and the
 * FILTERS popover leaves the untick behind rather than deleting the key — so the group being
 * present says nothing, only a ticked option in it does.
 */
export const filterGroupHasSelection = (group: unknown): boolean =>
  !!group && typeof group === "object" && Object.values(group).some(Boolean);

/**
 * Is anything currently narrowing the inventory list?
 *
 * This is what tells "this project has no building elements yet" apart from "the filters match
 * none of the ones it has" — the two states the inventory used to show the same empty card for
 * (RTLCA-5551). Everything that can hide a row counts, not just the FILTERS popover: the search
 * term and the expired-EPD toggle narrow the same list from elsewhere in the toolbar.
 *
 * The filter groups arrive already reduced to a boolean rather than as the raw
 * `activeMaterialFilters` object: that object's type is circular across the project and filters
 * stores, and handing it to a typed parameter makes TypeScript give up and widen both stores to
 * `any` (the same reason `activeMaterial` only ever reads it by property access).
 */
export const hasHidingInventoryFilters = (
  hasFilterGroupSelection: boolean,
  searchTerm: string | null | undefined,
  showExpiredEpdOnly: boolean
): boolean => hasFilterGroupSelection || (searchTerm ?? "").trim().length > 0 || showExpiredEpdOnly;

/**
 * Which models the inventory should show, given the toolbar's global Models selection.
 *
 * The inventory has no model filter of its own (RTLCA-5569) — the dashboard store's `selectedModels`
 * is the one model dimension, and it is a plain list of model names. An empty selection means "all",
 * matching how the dashboard endpoint reads it, and anything else is intersected with the models the
 * materials map actually holds: the selection comes from `BuildingProject.GetAllModelNames()`, a
 * superset of the map's keys, so a selected name with no building elements simply contributes none.
 */
export const modelsToShow = (allModelNames: string[], selectedModelNames: string[]): string[] =>
  selectedModelNames.length === 0 ? allModelNames : allModelNames.filter((name) => selectedModelNames.includes(name));

/**
 * Is the global Models selection hiding inventory rows?
 *
 * True for a non-empty selection that leaves at least one of the inventory's own models out — empty
 * means "all" (see `modelsToShow`), and a selection covering every one of them hides nothing. It is
 * measured against the models the materials map holds rather than the project's full model list, so
 * a name the selection carries that has no building elements behind it still counts as narrowing.
 *
 * This is the model leg of `hasActiveInventoryFilters`: without it, selecting a model that holds no
 * building elements leaves the inventory claiming the project has none rather than offering "Reset
 * filters" (RTLCA-5551's two empty states).
 */
export const modelSelectionNarrows = (allModelNames: string[], selectedModelNames: string[]): boolean =>
  selectedModelNames.length > 0 && modelsToShow(allModelNames, selectedModelNames).length < allModelNames.length;
